import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';

interface EquipmentFormData {
  name: { en: string; ar?: string };
  category: string;
  iconUrl: string;
  order: number;
  active: boolean;
}

const EQUIPMENT_CATEGORIES = ['utensil', 'cookware', 'appliance', 'tool', 'other'] as const;

export default function EquipmentForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [formData, setFormData] = useState<EquipmentFormData>({
    name: { en: '', ar: '' },
    category: 'other',
    iconUrl: '',
    order: 0,
    active: true,
  });

  useEffect(() => {
    if (isEditing && id) loadEquipment();
  }, [id]);

  const loadEquipment = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/admin/equipment/${id}`);
      const e = res.data?.data ?? res.data;
      if (!e) {
        alert(t('equipmentForm.failedToLoad'));
        navigate('/?tab=equipments');
        return;
      }
      setFormData({
        name: e.name ?? { en: '', ar: '' },
        category: e.category ?? 'other',
        iconUrl: e.iconUrl ?? '',
        order: e.order ?? 0,
        active: e.active !== false,
      });
    } catch (err) {
      console.error(err);
      alert(t('equipmentForm.failedToLoad'));
      navigate('/?tab=equipments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        category: formData.category,
        iconUrl: (formData.iconUrl || '').trim() || undefined,
        order: formData.order,
        active: formData.active,
      };
      if (isEditing && id) {
        await api.put(`/admin/equipment/${id}`, payload);
      } else {
        await api.post('/admin/equipment', payload);
      }
      navigate('/?tab=equipments');
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || t('equipmentForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex items-center justify-center min-h-[300px]">{t('equipmentForm.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isEditing ? t('equipmentForm.editEquipment') : t('equipmentForm.createEquipment')}
      subtitle={t('equipmentForm.subtitle')}
    >
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
          {/* Icon first */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipmentForm.icon')}</label>
            <div className="flex items-center gap-4">
              {formData.iconUrl ? (
                <div className="flex items-center gap-3">
                  <img src={formData.iconUrl} alt="Icon" className="w-14 h-14 object-contain rounded-lg border border-gray-200 bg-gray-50" />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, iconUrl: '' })}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    {t('equipmentForm.removeIcon')}
                  </button>
                </div>
              ) : null}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingIcon}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !file.type.startsWith('image/')) return;
                    setUploadingIcon(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      fd.append('folder', 'equipment-icons');
                      fd.append('type', 'image');
                      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      if (res.data?.success && res.data?.data?.url) {
                        setFormData({ ...formData, iconUrl: res.data.data.url });
                      } else throw new Error('Upload failed');
                    } catch (err: any) {
                      alert(err?.message || t('equipmentForm.failedToUploadIcon'));
                    } finally {
                      setUploadingIcon(false);
                      e.target.value = '';
                    }
                  }}
                />
                <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                  {uploadingIcon ? t('equipmentForm.uploadingIcon') : t('equipmentForm.uploadIcon')}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('equipmentForm.iconHelp')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipmentForm.nameEn')}</label>
            <input
              type="text"
              required
              value={formData.name.en}
              onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipmentForm.nameAr')}</label>
            <input
              type="text"
              value={formData.name.ar ?? ''}
              onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipmentForm.category')}</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {EQUIPMENT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{t(`equipments.${cat}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipmentForm.order')}</label>
            <input
              type="number"
              min={0}
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="equip-active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="rounded border-gray-300 text-primary-orange"
            />
            <label htmlFor="equip-active" className="text-sm font-medium text-gray-700">{t('equipmentForm.active')}</label>
          </div>
          <div className="flex flex-wrap justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/?tab=equipments')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('equipmentForm.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {t('equipmentForm.save')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
